const APPOINTMENT_LINK_FIELDS = [
  'integrationReference',
  'integrationClientParRef',
  'integrationStaffParRef',
  'integrationClientRef',
  'integrationStaffRef',
  'integrationAppointmentType',
  'integration',
  'integrationExternalUrl',
];

/**
 * Extracts the necessary fields from an appointment to link it to a session activity.
 * Only includes fields relevant for the session activity, ignoring others like status or sessionActivity reference.
 * @param {object} appointment - The appointment document to extract data from
 * @returns {object} An object containing only the fields needed for the session activity's appointmentData
 * @throws {Error} If the appointment is not in a linkable state or is already linked
 */
export function extractAppointmentLinkData(appointment) {
  return Object.fromEntries(
    APPOINTMENT_LINK_FIELDS
      .filter((key) => key in appointment)
      .map((key) => [key, appointment[key]]),
  );
}

/**
 * Verifies that the given appointment and session activity can be linked together.
 * Checks that neither is already linked and that the appointment is in a 'scheduled' state.
 * @throws {Error} If the appointment or session activity is not linkable
 * @param {object} appointment - The appointment document to check
 * @param {object} sessionActivity - The session activity document to check
 * @param {string} appointmentId - The ID of the appointment
 * @param {string} sessionActivityId - The ID of the session activity
 */
export function assertLinkable(appointment, sessionActivity, appointmentId, sessionActivityId) {
  if (appointment.sessionActivity !== null && appointment.sessionActivity !== undefined) {
    throw new Error(`Appointment ${appointmentId} is already linked to a session activity`);
  }
  if (appointment.status !== 'scheduled') {
    throw new Error(
      `Appointment ${appointmentId} is not in a linkable state (status: ${appointment.status})`,
    );
  }
  if (sessionActivity.appointment !== null && sessionActivity.appointment !== undefined) {
    throw new Error(`Session activity ${sessionActivityId} is already linked to an appointment`);
  }
}

/**
 * Builds the update object for linking an appointment to a session activity.
 * @param {string} sessionActivityId - The ID of the session activity
 * @param {object} sessionActivity - The session activity document
 * @returns {object} The update object for the appointment
 */
export function buildAppointmentLinkUpdate(sessionActivityId, sessionActivity) {
  return {
    sessionActivity: sessionActivityId,
    status: 'completed',
    session: sessionActivity.session ?? null,
  };
}

/**
 * Builds the update object for linking a session activity to an appointment.
 * @param {string} appointmentId - The ID of the appointment
 * @param {object} appointmentData - The data extracted from the appointment
 * @returns {object} The update object for the session activity
 */
export function buildSessionActivityLinkUpdate(appointmentId, appointmentData) {
  return {
    appointment: appointmentId,
    appointmentData,
  };
}
